<template>
  <div :class="classObj" class="app-wrapper" :style="appWrapperStyle">
    <div v-if="device === 'mobile' && sidebar.opened" class="drawer-bg" @click="handleClickOutside" />

    <!-- スマホ全ページ：左縁メニュー（旧 mobile-header の bars は廃止） -->
    <button
      v-if="device === 'mobile' && !sidebar.opened"
      type="button"
      class="mobile-edge-toggle"
      :class="{ 'is-revealed': edgeMenuRevealed }"
      @click="handleToggleSidebar"
    >
      <i class="fas fa-bars"></i>
    </button>

    <sidebar v-if="!sidebar.hide" class="sidebar-container" :style="{ width: device === 'mobile' ? '100% !important' : 'var(--sidebar-width)' }" @setLayout="setLayout" />
    <button v-if="sidebar.hide && device !== 'mobile'" type="button" class="sidebar-reveal-btn"
      :title="$t('global.showSidebar')" @click="useAppStore().toggleSideBarHide(false)">
      <i class="fas fa-angles-right"></i>
    </button>
    <div :class="{ hasTagsView: needTagsView, sidebarHide: sidebar.hide }" class="main-container">
      <!-- pc端显示旧UI -->
      <!-- <div :class="{ 'fixed-header': fixedHeader }" v-if="device === 'desktop'">
        <navbar @setLayout="setLayout" />
        <tags-view v-if="needTagsView" />
      </div> -->
      <app-main />
      <settings ref="settingRef" />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watchEffect, onMounted, onUnmounted, watch } from 'vue'
import { useWindowSize } from '@vueuse/core'
import Sidebar from './components/Sidebar/index.vue'
import { AppMain, Navbar, Settings, TagsView } from './components'
import defaultSettings from '@/settings'
import { useRoute } from 'vue-router';
import useAppStore from '@/store/modules/app'
import useSettingsStore from '@/store/modules/settings'

const settingsStore = useSettingsStore()
const theme = computed(() => settingsStore.theme);
const sideTheme = computed(() => settingsStore.sideTheme);
const sidebar = computed(() => useAppStore().sidebar);
const device = computed(() => useAppStore().device);
const needTagsView = computed(() => settingsStore.tagsView);
const fixedHeader = computed(() => settingsStore.fixedHeader);

const appWrapperStyle = computed(() => {
  const style = { '--current-color': theme.value };
  if (device.value === 'mobile') {
    // mobile-header 廃止：トップ余白なし（ブランドはサイドバーの明暗切替行へ）
    style['--mobile-header-height'] = '0px';
  }
  return style;
});

const classObj = computed(() => ({
  hideSidebar: !sidebar.value.opened,
  openSidebar: sidebar.value.opened,
  withoutAnimation: sidebar.value.withoutAnimation,
  mobile: device.value === 'mobile'
}))

const { width, height } = useWindowSize();
const WIDTH = 992; // refer to Bootstrap's responsive design
const previousDevice = ref(null);

const route = useRoute();

/** 左縁メニュー：デフォルトは縁に収納、タップ/スクロールで露出 */
const edgeMenuRevealed = ref(false)
const EDGE_MENU_HIDE_MS = 2200
let edgeMenuHideTimer = null
let edgeMenuListenersBound = false

function clearEdgeMenuHideTimer() {
  if (edgeMenuHideTimer) {
    window.clearTimeout(edgeMenuHideTimer)
    edgeMenuHideTimer = null
  }
}

function scheduleEdgeMenuHide() {
  clearEdgeMenuHideTimer()
  if (sidebar.value.opened) return
  edgeMenuHideTimer = window.setTimeout(() => {
    edgeMenuHideTimer = null
    edgeMenuRevealed.value = false
  }, EDGE_MENU_HIDE_MS)
}

function revealEdgeMenu() {
  if (device.value !== 'mobile') return
  edgeMenuRevealed.value = true
  scheduleEdgeMenuHide()
}

function onEdgeMenuUserActivity() {
  revealEdgeMenu()
}

function bindEdgeMenuListeners() {
  if (edgeMenuListenersBound || typeof window === 'undefined') return
  edgeMenuListenersBound = true
  const opts = { passive: true, capture: true }
  document.addEventListener('pointerdown', onEdgeMenuUserActivity, opts)
  document.addEventListener('touchstart', onEdgeMenuUserActivity, opts)
  document.addEventListener('touchmove', onEdgeMenuUserActivity, opts)
  document.addEventListener('scroll', onEdgeMenuUserActivity, opts)
}

function unbindEdgeMenuListeners() {
  if (!edgeMenuListenersBound || typeof window === 'undefined') return
  edgeMenuListenersBound = false
  document.removeEventListener('pointerdown', onEdgeMenuUserActivity, true)
  document.removeEventListener('touchstart', onEdgeMenuUserActivity, true)
  document.removeEventListener('touchmove', onEdgeMenuUserActivity, true)
  document.removeEventListener('scroll', onEdgeMenuUserActivity, true)
  clearEdgeMenuHideTimer()
  edgeMenuRevealed.value = false
}

function syncEdgeMenuListeners() {
  if (device.value === 'mobile') {
    bindEdgeMenuListeners()
  } else {
    unbindEdgeMenuListeners()
  }
}

// 初始化设备类型
onMounted(() => {
  const isMobile = width.value - 1 < WIDTH;
  previousDevice.value = isMobile ? 'mobile' : 'desktop';
  useAppStore().toggleDevice(previousDevice.value);

  if (previousDevice.value === 'mobile' && sidebar.value.opened) {
    useAppStore().closeSideBar({ withoutAnimation: true });
  } else if (previousDevice.value === 'desktop' && !sidebar.value.opened) {
    // デスクトップでは折りたたみ（54px）状態を持たせない：Cookie 由来の collapse を強制的に展開へ戻す
    useAppStore().openSideBar(true);
  }
  syncEdgeMenuListeners()
});

watch(
  () => device.value,
  () => {
    syncEdgeMenuListeners()
  },
)

watch(
  () => sidebar.value.opened,
  (opened) => {
    if (device.value !== 'mobile') return
    if (opened) {
      clearEdgeMenuHideTimer()
      edgeMenuRevealed.value = true
    } else {
      scheduleEdgeMenuHide()
    }
  },
)

onUnmounted(() => {
  unbindEdgeMenuListeners()
});

watchEffect(() => {
  const isMobile = width.value - 1 < WIDTH;
  const currentDevice = isMobile ? 'mobile' : 'desktop';

  // 只在设备类型改变时执行操作
  if (previousDevice.value !== currentDevice) {
    const oldDevice = previousDevice.value;
    previousDevice.value = currentDevice;
    useAppStore().toggleDevice(currentDevice);

    // 只在从桌面端切换到移动端时关闭 sidebar
    if (currentDevice === 'mobile' && oldDevice === 'desktop' && sidebar.value.opened) {
      useAppStore().closeSideBar({ withoutAnimation: true });
    } else if (currentDevice === 'desktop' && oldDevice === 'mobile' && !sidebar.value.opened) {
      // 移动端 → 桌面端に戻ったら、折りたたみ（54px）ではなく常に展開ラベル付きUIにする
      useAppStore().openSideBar(true);
    }
  }
})

function handleClickOutside() {
  useAppStore().closeSideBar({ withoutAnimation: false })
}

function handleToggleSidebar(e) {
  e.preventDefault();
  e.stopPropagation();
  const appStore = useAppStore();
  // 确保在移动端时，可以正常切换 sidebar
  if (appStore.sidebar.hide) {
    return;
  }
  if (device.value === 'mobile') {
    edgeMenuRevealed.value = true
    clearEdgeMenuHideTimer()
  }
  appStore.toggleSideBar(false);
}

const settingRef = ref(null);
function setLayout() {
  settingRef.value.openSetting();
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/mixin.scss";
@import "@/assets/styles/variables.module.scss";

.app-wrapper {
  @include clearfix;
  position: relative;
  height: 100%;
  width: 100%;

  &.mobile {
    --mobile-header-height: 0px; /* mobile-header 廃止 */
  }
  &.mobile.openSidebar {
    position: fixed;
    top: 0;
  }
}

// 侧边栏被手动隐藏后，用于重新呼出侧边栏的悬浮按钮
.sidebar-reveal-btn {
  position: fixed;
  left: 0;
  bottom: 76px;
  z-index: 101;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 44px;
  border: 1px solid var(--border-color);
  border-left: none;
  border-radius: 0 6px 6px 0;
  background: var(--bg-panel);
  color: var(--text-secondary);
  box-shadow: var(--shadow-md);
  cursor: pointer;
  transition: color 0.2s, width 0.2s;

  &:hover {
    width: 24px;
    color: var(--brand-color);
  }
}

.drawer-bg {
  background: #000;
  opacity: 0.3;
  width: 100%;
  top: 0;
  height: 100%;
  position: absolute;
  z-index: 999;
}

.fixed-header {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 9;
  width: calc(100% - var(--sidebar-width, #{$base-sidebar-width}));
  transition: width 0.28s;
}

.hideSidebar .fixed-header {
  width: calc(100% - 54px);
}

.sidebarHide .fixed-header {
  width: 100%;
}

.mobile .fixed-header {
  width: 100%;
}

/* 移动端：mobile-header 廃止後は余白なし */
.mobile .main-container {
  padding-top: var(--mobile-header-height, 0px);
}

/* スマホ全ページ：メニューは左縁に収納、タップ/スクロールで露出 */
.mobile-edge-toggle {
  position: fixed;
  left: 0;
  top: 50%;
  z-index: 1302;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 46px;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 0 14px 14px 0;
  background: var(--brand-color, #f9da49);
  color: #fff; // 浅色红底用白图标保证对比；深色黄底见下方 html.dark 覆盖
  font-size: 18px;
  cursor: pointer;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.22);
  /* 默认大部分收入左缘，只留细条可点 */
  transform: translate(-72%, -50%);
  transition: transform 0.28s cubic-bezier(0.22, 0.8, 0.25, 1), width 0.15s ease, filter 0.15s ease;

  &.is-revealed {
    transform: translate(0, -50%);
  }

  &:hover {
    width: 46px;
    filter: brightness(1.05);
  }

  &:active {
    filter: brightness(0.95);
  }
}


</style>
